import { useEffect, useRef, useState, useCallback } from "react";

type Props = {
  end: number;
  /** Duration on scroll-in (ms) */
  duration?: number;
  /** Duration when re-triggered by hover (ms) */
  hoverDuration?: number;
  /** Keep the number gently increasing while visible, then loop back */
  loop?: boolean;
  loopRange?: number;
  loopInterval?: number;
  suffix?: string;
  className?: string;
};

/**
 * CountUp — premium, easing-driven number counter.
 *
 * - Auto-animates once when scrolled into view (slow, calm pace).
 * - Re-runs on parent hover via the `group-hover` pattern: place CountUp
 *   inside an element with the `group` class. A `data-countup` attribute
 *   is added so the parent can trigger replays through a custom event,
 *   but we also listen to native `mouseenter` on the closest `.group`.
 */
export const CountUp = ({
  end,
  duration = 4200,
  hoverDuration = 3200,
  loop = false,
  loopRange = 99,
  loopInterval = 1350,
  suffix = "",
  className,
}: Props) => {
  const [value, setValue] = useState(loop ? end : 0);
  const ref = useRef<HTMLSpanElement>(null);
  const rafRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const hasStartedRef = useRef(false);
  const isAnimatingRef = useRef(false);

  // easeOutQuint — calm, smooth deceleration so big numbers don't feel rushed
  const ease = (t: number) => 1 - Math.pow(1 - t, 5);

  const animate = useCallback(
    (ms: number) => {
      if (typeof window === "undefined") return;
      const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (prefersReduced) {
        setValue(end);
        return;
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      isAnimatingRef.current = true;
      const start = performance.now();
      const from = 0;
      const tick = (now: number) => {
        const progress = Math.min((now - start) / ms, 1);
        const eased = ease(progress);
        setValue(Math.round(from + (end - from) * eased));
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          isAnimatingRef.current = false;
          rafRef.current = null;
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    },
    [end],
  );

  const stopLoop = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startLoop = useCallback(() => {
    if (typeof window === "undefined") return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    stopLoop();
    if (prefersReduced) {
      setValue(end);
      return;
    }
    setValue(end);
    intervalRef.current = window.setInterval(() => {
      setValue((current) => {
        const offset = Math.max(0, current - end);
        return offset >= loopRange ? end : current + 1;
      });
    }, loopInterval);
  }, [end, loopInterval, loopRange, stopLoop]);

  // Scroll-in trigger
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (loop) {
            if (e.isIntersecting) {
              hasStartedRef.current = true;
              startLoop();
            } else {
              stopLoop();
            }
            return;
          }
          if (e.isIntersecting && !hasStartedRef.current) {
            hasStartedRef.current = true;
            animate(duration);
            io.disconnect();
          }
        });
      },
      { threshold: 0.35 },
    );
    io.observe(node);
    return () => {
      io.disconnect();
      stopLoop();
    };
  }, [animate, duration, loop, startLoop, stopLoop]);

  // Hover replay — listen on the closest `.group` ancestor (the card)
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const card = node.closest(".group") as HTMLElement | null;
    if (!card) return;

    const onEnter = () => {
      // Avoid restarting mid-initial-animation; allow replay otherwise
      if (loop) return;
      if (!hasStartedRef.current) return;
      if (isAnimatingRef.current) return;
      animate(hoverDuration);
    };
    card.addEventListener("mouseenter", onEnter);
    return () => card.removeEventListener("mouseenter", onEnter);
  }, [animate, hoverDuration, loop]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      stopLoop();
    };
  }, [stopLoop]);

  return (
    <span ref={ref} className={className}>
      {value.toLocaleString()}
      {suffix}
    </span>
  );
};

export default CountUp;
